import type { RawQuestion, RawVariable } from '@/features/content/useComponent';
import {
  resolveRepresentativeVariableValues,
  substituteVariablesInContent,
} from '@/features/questions/randomVariables';

import type { EditableQuestion } from './editorTypes';

export type SubstituteFn = (html: string) => string;

export const identitySubstitute: SubstituteFn = (html) => html;

/**
 * Build a substitution function that replaces an editable question's `##VAR##`
 * placeholders with a deterministic REPRESENTATIVE set of values, so the
 * placeholders never reach the KaTeX parser in any read-only preview (the editor
 * preview card AND the content page's edit-mode question list). Returns the
 * identity function when the question has no random variables. Multi-part
 * questions share the parent's variables, so this is computed once on the parent
 * and threaded down to its parts.
 *
 * Note: only read-only PREVIEWS use this. The actual editing surface (the Tiptap
 * box in the edit dialog) still shows the raw `##VAR##` notation.
 */
export function buildRepresentativeSubstitute(question: EditableQuestion): {
  hasRepresentativeValues: boolean;
  substitute: SubstituteFn;
  /** The values behind the substitution, for anything that needs the numbers
      themselves rather than the rendered text — the preview's computed answer. */
  values: RawVariable[];
} {
  if (!question.useRandomVariables || question.variables.length === 0) {
    return {
      hasRepresentativeValues: false,
      substitute: identitySubstitute,
      values: [],
    };
  }

  const rawQuestion: RawQuestion = {
    content: question.contentHtml,
    seed_for_random_variables: question.seedForRandomVariables,
    variable_based_answer: question.variableBasedAnswer,
    variables: question.variables.map((variable) => ({
      max_value: variable.maxValue,
      min_value: variable.minValue,
      name: variable.name,
      type: variable.type,
    })),
  };

  const values = resolveRepresentativeVariableValues(
    rawQuestion,
    question.type === 'MULTI_PART',
  );

  if (values.length === 0) {
    return {
      hasRepresentativeValues: false,
      substitute: identitySubstitute,
      values: [],
    };
  }

  return {
    hasRepresentativeValues: true,
    substitute: (html) => substituteVariablesInContent(html, values),
    values,
  };
}
